import { useEffect, useState } from 'react';
import { Alert, Card, Chip } from '@heroui/react';
import { fetchSchedule, type Occurrence } from '../data/api.ts';
import { groupByDay, ZONES } from '../lib/schedule.ts';
import { ScheduleTable } from '../components/ScheduleTable.tsx';
import { PageLoader } from '../components/PageLoader.tsx';
import { errorText } from '../lib/errors.ts';

/**
 * Upcoming activities (DECISION-12).
 *
 * The API returns absolute instants; both timezones are rendered via Intl. That
 * is deliberate — Warsaw observes DST, so its offset from Moscow is 1h for part
 * of the year and 2h for the rest. Storing local times would be wrong for half
 * the year.
 *
 * The day grouping, «Сегодня», «идёт» and the dimming of what is over all live
 * in `lib/schedule.ts`, as pure functions with tests; the rows themselves are
 * `ScheduleTable`, shared with the home page. This file is the page around
 * them. Date arithmetic across two zones, one of which observes DST, has no
 * interesting case that is reachable from the page on the afternoon you write
 * it — hence the split.
 */

/**
 * How often the page re-reads the clock.
 *
 * ⚠️ WITHOUT THIS NOTHING EVER LIGHTS UP. `now` is read once per render, and a
 * schedule left open on a phone renders once — so a page opened at 20:55 would
 * still be showing the 21:00 raid as "not yet" at 21:30, and «Сегодня» would
 * sit on yesterday until somebody reloaded. Half a minute is finer than the
 * minute these times are quoted to and costs one comparison per row.
 */
const CLOCK_TICK_MS = 30_000;

export function SchedulePage() {
  const [occurrences, setOccurrences] = useState<Occurrence[] | null>(null);
  /** The LOAD's failure. This page has no writes; see `lib/notify.tsx`. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    fetchSchedule(21)
      .then((response) => setOccurrences(response.occurrences))
      .catch((caught: unknown) => setLoadError(errorText(caught, 'Не удалось загрузить расписание.')));
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const days = groupByDay(occurrences ?? [], now);

  return (
    <div className="fade-in mx-auto flex w-full max-w-3xl flex-col gap-4">
      {/* The heading that used to sit above this line is gone — the nav cell
          already says «Расписание». `AppShell` keeps the h1. */}
      <p className="text-sm text-muted">
        Время показано в поясах: {ZONES.map((zone) => zone.label).join(' / ')}. Основной — Москва.
      </p>

      {loadError && <Alert status="danger">{loadError}</Alert>}

      {occurrences === null && !loadError && <PageLoader />}

      {occurrences?.length === 0 && (
        <Card>
          <Card.Content className="flex flex-col gap-1">
            <p className="text-sm text-muted">В ближайшие три недели ничего не запланировано.</p>
            <p className="text-xs text-muted">
              Админы добавляют активности в разделе Админка → Расписание.
            </p>
          </Card.Content>
        </Card>
      )}

      {days.map((day) => (
        <Card key={day.key}>
          <Card.Header>
            <div className="flex flex-wrap items-center gap-2">
              {/* Intl gives «четверг, 7 августа» lowercase; the capital is
                  presentation, so it is a CSS rule rather than a second string. */}
              <Card.Title className="text-base first-letter:uppercase">{day.label}</Card.Title>
              {/* Only ever on one card, and on none at all when nothing is
                  scheduled for today — which is an ordinary state, not a gap.
                  Today is the card people are scrolling to find. */}
              {day.isToday && (
                <Chip size="sm" variant="soft" color="accent">
                  Сегодня
                </Chip>
              )}
            </div>
          </Card.Header>

          <Card.Content>
            <ScheduleTable items={day.items} ariaLabel={`Расписание на ${day.label}`} />
          </Card.Content>
        </Card>
      ))}
    </div>
  );
}
